import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import myAccountStore from './my-account-store';
import { getMyOrganizations } from './api';

const isLoading = ref(false);
const useMyOrganization = (loadImmediately = false) => {
  const store = myAccountStore();
  const { organizations } = storeToRefs(store);
  const { reset, setOrganization } = store;
  const isFetchingOrganizations = ref<boolean>(false);

  const shouldRefreshData = (): boolean => {
    // should refresh data if there is org since users without org isn't allowed.
    if (!organizations.value?.data?.length) {
      return true;
    }

    const hour = 60 * 60 * 1000;
    if (Date.now() - organizations.value?.updatedAt > hour * 2) {
      return true;
    }
    return false;
  };

  /**
   *
   * Load all user organizations and save to the persistent store if the data doesnt't exist or older than constraints (2 hours).
   * "isLoading" ref is for tracing the loading.
   * "organizations" where the data will be held.
   */
  const loadMyOrganizations = async () => {
    if (!shouldRefreshData()) {
      return;
    }

    isLoading.value = true;
    try {
      const chunkSize = 100;
      const { data, meta } = await getMyOrganizations(chunkSize, 0);
      const leftOrganizations = meta.totalCount - data.length;

      if (leftOrganizations > 0) {
        const count = Math.ceil(leftOrganizations / chunkSize);
        const calls = Array.from({ length: count }).map((_, i) => getMyOrganizations(100, i + 1));
        const responses = await Promise.all(calls);
        data.push(...responses.flatMap((r) => r.data));
      }

      setOrganization({ data, meta, updatedAt: Date.now() });
    } finally {
      isLoading.value = false;
    }
  };

  if (loadImmediately && !isLoading.value) {
    loadMyOrganizations();
  }

  /**
   * direct access to API.
   * "isFetchingOrganizations" ref is used for tracking fetching
   * No error handling done.
   */
  const fetchMyOrganizations = (limit: number, offset: number) => {
    isFetchingOrganizations.value = true;
    return getMyOrganizations(limit, offset).finally(() => {
      isFetchingOrganizations.value = false;
    });
  };

  return {
    fetchMyOrganizations,
    isLoading,
    loadMyOrganizations,
    organizations,
    reset,
  };
};

export default useMyOrganization;
